import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanagelapi.stechomeyazilim.info";

const IadeTalepleri = () => {
  document.title = "İade Talepleri | HarmanaGEL";

  const [iadeler, setIadeler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [selectedIade, setSelectedIade] = useState(null);
  const [newDurum, setNewDurum] = useState("");

  const fetchIadeler = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/IADE_TALEPLERI?$top=500&$orderby=TALEP_TARIHI desc&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress)`
      );
      const result = await response.json();
      if (result && result.value) setIadeler(result.value);
    } catch (err) {
      setError("İade talepleri yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIadeler();
  }, []);

  const handleEdit = (iade) => {
    setSelectedIade(iade);
    setNewDurum(iade.DURUM || "Beklemede");
    setModal(true);
  };

  const handleSave = async () => {
    try {
      await fetch(`${BASE_URL}/IADE_TALEPLERI(${selectedIade.ID})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          DURUM: newDurum,
          SONUCLANMA_TARIHI: ["Onaylandı", "Reddedildi"].includes(newDurum) ? new Date().toISOString() : null,
        }),
      });
      setSuccessMsg("İade talebi güncellendi.");
      setModal(false);
      fetchIadeler();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("Güncelleme sırasında hata oluştu.");
    }
  };

  const getDurumBadge = (durum) => {
    const renkler = {
      "Beklemede": "warning",
      "İnceleniyor": "info",
      "Onaylandı": "success",
      "Reddedildi": "danger",
      "Kargo Bekleniyor": "primary",
    };
    return <Badge color={renkler[durum] || "secondary"}>{durum || "Bilinmiyor"}</Badge>;
  };

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Müşteri",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const u = cell.getValue();
        return u ? (
          <div>
            <div className="fw-medium">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : `ID: ${cell.row.original.KULLANICI_ID}`;
      },
    },
    {
      header: "İade Nedeni",
      accessorKey: "IADE_NEDENI",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => (
        <span title={cell.row.original.ACIKLAMA || ""}>
          {cell.getValue()}
        </span>
      ),
    },
    {
      header: "İade Tutarı",
      accessorKey: "IADE_TUTARI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => (
        <span className="fw-bold text-danger">
          {parseFloat(cell.getValue() || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
        </span>
      ),
    },
    {
      header: "Kargo Kodu",
      accessorKey: "KARGO_KODU",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => cell.getValue() ? (
        <span className="font-monospace small">{cell.getValue()}</span>
      ) : <span className="text-muted">-</span>,
    },
    {
      header: "Durum",
      accessorKey: "DURUM",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => getDurumBadge(cell.getValue()),
    },
    {
      header: "Talep Tarihi",
      accessorKey: "TALEP_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleDateString("tr-TR") : "-";
      },
    },
    {
      header: "Sonuçlanma",
      accessorKey: "SONUCLANMA_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleDateString("tr-TR") : "-";
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => (
        <Button color="primary" size="sm" outline onClick={() => handleEdit(cellProps.row.original)}>
          <i className="bx bx-edit" />
        </Button>
      ),
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="İade Talepleri" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">İade Talepleri</h4>
                    <Button color="primary" size="sm" onClick={fetchIadeler} disabled={isLoading}>
                      <i className="bx bx-refresh me-1" />
                      Yenile
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={iadeler}
                      isGlobalFilter={true}
                      SearchPlaceholder="İade talebi ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered>
        <ModalHeader toggle={() => setModal(false)}>
          İade Talebi #{selectedIade?.ID}
        </ModalHeader>
        <ModalBody>
          {selectedIade && (
            <>
              <div className="mb-3">
                <label className="form-label">İade Nedeni</label>
                <p className="form-control-plaintext bg-light p-2 rounded">{selectedIade.IADE_NEDENI}</p>
              </div>
              {selectedIade.ACIKLAMA && (
                <div className="mb-3">
                  <label className="form-label">Açıklama</label>
                  <p className="text-muted">{selectedIade.ACIKLAMA}</p>
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">İade Tutarı</label>
                <p className="fw-bold text-danger fs-5">
                  {parseFloat(selectedIade.IADE_TUTARI || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
                </p>
              </div>
              <div className="mb-3">
                <label className="form-label">Durumu Güncelle</label>
                <Input
                  type="select"
                  value={newDurum}
                  onChange={(e) => setNewDurum(e.target.value)}
                >
                  <option value="Beklemede">Beklemede</option>
                  <option value="İnceleniyor">İnceleniyor</option>
                  <option value="Kargo Bekleniyor">Kargo Bekleniyor</option>
                  <option value="Onaylandı">Onaylandı</option>
                  <option value="Reddedildi">Reddedildi</option>
                </Input>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)}>İptal</Button>
          <Button color="primary" onClick={handleSave}>Güncelle</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default IadeTalepleri;
